import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Copy, KeyRound, RotateCcw, ShieldCheck, Video } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/meeting-server',
  component: MeetingServerSettings,
})

function MeetingServerSettings() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api.getAppSettings(),
    enabled: !!meData?.user,
  })

  const [baseUrl, setBaseUrl] = useState('')
  const [appId, setAppId] = useState('')
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null)

  // Hydrate form from server state once
  useEffect(() => {
    if (!data) return
    setBaseUrl(data.settings.meetingBaseUrl ?? '')
    setAppId(data.settings.meetingJwtAppId)
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      api.updateAppSettings({
        meetingBaseUrl: baseUrl.trim() || null,
        meetingJwtAppId: appId.trim() || 'embertime',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-settings'] })
      notify.success('Einstellungen gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const rotate = useMutation({
    mutationFn: () => api.rotateAppJwtSecret(),
    onSuccess: (r) => {
      setRotatedSecret(r.secret)
      qc.invalidateQueries({ queryKey: ['app-settings'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/settings/profile" />

  const secretSet = data?.settings.meetingJwtSecretSet ?? false

  return (
    <SettingsLayout
      title="Meeting-Server"
      description="Wo Ember Online-Meetings hostet. Beim Anlegen wird die URL automatisch befüllt; pro Meeting wird ein zufälliger Raum-Slug generiert."
    >
      <div className="space-y-4 max-w-2xl">
        {isLoading ? (
          <p className="text-sm text-text-tertiary">Lade …</p>
        ) : (
          <>
            <section className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-5">
              <header className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
                  <Video className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-serif text-xl tracking-tight">Server-URL</h2>
                  <p className="mt-1 text-sm text-text-tertiary">
                    Basis-URL der Jitsi-Instanz. Pro Meeting wird ein zufälliger Slug angehängt,
                    z.B. <code className="text-text-secondary">https://meet…/embertime-x7k9p2</code>
                    .
                  </p>
                </div>
              </header>

              <div className="space-y-2">
                <Label htmlFor="m-base">Meeting-Server-URL</Label>
                <Input
                  id="m-base"
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://meet.it.financeflow.de"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-appid">JWT-App-ID</Label>
                <Input
                  id="m-appid"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="embertime"
                />
                <p className="text-xs text-text-tertiary">
                  Muss mit dem <code className="text-text-secondary">JWT_APP_ID</code> in der
                  Prosody-Config übereinstimmen.
                </p>
              </div>
            </section>

            <section className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-5">
              <header className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
                  <KeyRound className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-serif text-xl tracking-tight">JWT-Secret</h2>
                  <p className="mt-1 text-sm text-text-tertiary">
                    HMAC-Schlüssel, mit dem Ember Join-Tokens signiert. Muss identisch im
                    Jitsi-K8s-Secret (<code className="text-text-secondary">jitsi-secrets</code>)
                    liegen, damit Prosody die Tokens validiert.
                  </p>
                </div>
              </header>

              <div className="flex items-center gap-3 text-sm">
                <ShieldCheck
                  className={
                    secretSet ? 'h-4 w-4 text-state-success' : 'h-4 w-4 text-state-warning'
                  }
                />
                <span>
                  {secretSet
                    ? 'Secret ist gesetzt (Wert wird aus Sicherheitsgründen nicht angezeigt).'
                    : 'Kein Secret konfiguriert — beim ersten Aufruf wird eines automatisch generiert.'}
                </span>
              </div>

              {rotatedSecret && (
                <div className="rounded-lg border border-state-warning/40 bg-state-warning/5 p-4 space-y-2">
                  <p className="text-sm font-medium text-state-warning">
                    Neues Secret — jetzt kopieren!
                  </p>
                  <p className="text-xs text-text-secondary">
                    Wird nur dieses eine Mal angezeigt. Trag den Wert in den K8s-Secret{' '}
                    <code className="text-text-secondary">jitsi-secrets</code> ein (Schlüssel{' '}
                    <code className="text-text-secondary">JWT_APP_SECRET</code>) und rollout die
                    Jitsi-Pods (
                    <code className="text-text-secondary">
                      kubectl -n jitsi rollout restart deployment
                    </code>
                    ), sonst schlagen neue Join-Versuche fehl.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 break-all rounded bg-bg-overlay/60 px-2 py-1.5 text-xs font-mono">
                      {rotatedSecret}
                    </code>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(rotatedSecret)
                        notify.success('Kopiert')
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'JWT-Secret rotieren?',
                      description:
                        'Bestehende Jitsi-Sessions laufen weiter, neue Beitritts-Versuche schlagen aber so lange fehl, bis du den neuen Wert auch in den K8s-Secret einträgst.',
                      confirmLabel: 'Rotieren',
                      variant: 'danger',
                    })
                    if (ok) rotate.mutate()
                  }}
                  disabled={rotate.isPending}
                  className="text-state-warning hover:bg-state-warning/10"
                >
                  <RotateCcw className="h-4 w-4" /> Secret rotieren
                </Button>
              </div>
            </section>

            {/* Speichern am Formularende — nicht mitten zwischen den Feldern (C9) */}
            <div className="flex justify-end">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                Speichern
              </Button>
            </div>
          </>
        )}
      </div>
    </SettingsLayout>
  )
}
